import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row, Col, Card, Statistic, Tag, Badge, Table, Typography,
  Space, Button, Progress, Alert, Skeleton, Tooltip,
  Divider,
} from "antd";
import {
  ProjectOutlined, ThunderboltOutlined, TrophyOutlined,
  ClockCircleOutlined, WarningOutlined, RiseOutlined,
  ArrowRightOutlined, ExclamationCircleOutlined, CheckCircleOutlined,
} from "@ant-design/icons";
import { reportingApi } from "../api/client";
import type { DashboardKPIs } from "../types";
import dayjs from "dayjs";

const { Title, Text } = Typography;

// ── RAG Badge ─────────────────────────────────────────────────────────────────
const RAGDot = ({ value }: { value: "Verde" | "Ámbar" | "Rojo" }) => {
  const colors: Record<string, string> = { Verde: "#52c41a", Ámbar: "#faad14", Rojo: "#ff4d4f" };
  return (
    <span
      style={{
        display: "inline-block",
        width: 10, height: 10,
        borderRadius: "50%",
        background: colors[value] ?? "#d9d9d9",
        marginRight: 6,
      }}
    />
  );
};

// ── RAG Gauge Card ────────────────────────────────────────────────────────────
function RAGGauge({ verde, ambar, rojo }: { verde: number; ambar: number; rojo: number }) {
  const total = verde + ambar + rojo || 1;
  return (
    <Card
      size="small"
      title={<Text strong style={{ fontSize: 13 }}>Estado RAG · Proyectos activos</Text>}
      style={{ height: "100%" }}
    >
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        {[
          { label: "Verde", count: verde, color: "#52c41a" },
          { label: "Ámbar", count: ambar, color: "#faad14" },
          { label: "Rojo", count: rojo, color: "#ff4d4f" },
        ].map(({ label, count, color }) => (
          <div key={label}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <Space size={6}>
                <RAGDot value={label as any} />
                <Text style={{ fontSize: 13 }}>{label}</Text>
              </Space>
              <Text strong>{count}</Text>
            </div>
            <Progress
              percent={Math.round((count / total) * 100)}
              showInfo={false}
              strokeColor={color}
              trailColor="#f0f0f0"
              size="small"
            />
          </div>
        ))}
      </Space>
    </Card>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KPICard({
  title, value, icon, color, suffix, onClick, alert,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  suffix?: string;
  onClick?: () => void;
  alert?: boolean;
}) {
  return (
    <Card
      size="small"
      hoverable={!!onClick}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        borderLeft: `3px solid ${alert && value > 0 ? "#ff4d4f" : color}`,
        height: "100%",
      }}
    >
      <Statistic
        title={<Text style={{ fontSize: 12, color: "#666" }}>{title}</Text>}
        value={value}
        suffix={suffix}
        prefix={React.cloneElement(icon as React.ReactElement, { style: { color, fontSize: 18 } })}
        valueStyle={{ fontSize: 26, fontWeight: 700, color: alert && value > 0 ? "#ff4d4f" : "#1a1f5e" }}
      />
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState<DashboardKPIs | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportingApi.dashboard()
      .then((r) => setKpis(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const hoy = dayjs().format("dddd, D [de] MMMM [de] YYYY");

  if (loading) {
    return (
      <div>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
        <Row gutter={[16, 16]}>
          {[...Array(6)].map((_, i) => <Col key={i} xs={24} sm={12} lg={4}><Skeleton.Button active block style={{ height: 100 }} /></Col>)}
        </Row>
      </div>
    );
  }

  const rag = kpis?.proyectos_rag ?? { Verde: 0, Ámbar: 0, Rojo: 0 };

  return (
    <div>
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <Space style={{ justifyContent: "space-between", width: "100%", alignItems: "flex-start" }}>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>
              Dashboard ejecutivo
            </Title>
            <Text style={{ color: "#888", fontSize: 13, textTransform: "capitalize" }}>{hoy}</Text>
          </div>
          <Button
            type="primary"
            icon={<RiseOutlined />}
            onClick={() => navigate("/reporting")}
            style={{ background: "#1a1f5e" }}
          >
            Ver reporte semanal
          </Button>
        </Space>
      </div>

      {/* ── Alertas críticas ─────────────────────────────────────────────── */}
      {kpis && (rag["Rojo"] > 0 || kpis.proyectos_sin_actualizar > 0 || kpis.cai_pendientes > 0) && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 20, borderLeft: "3px solid #faad14" }}
          message={
            <Space split={<Divider type="vertical" />} wrap>
              {rag["Rojo"] > 0 && (
                <Text>
                  <RAGDot value="Rojo" />
                  <strong>{rag["Rojo"]}</strong> proyecto{rag["Rojo"] > 1 ? "s" : ""} en estado Rojo
                </Text>
              )}
              {kpis.proyectos_sin_actualizar > 0 && (
                <Text>
                  <ClockCircleOutlined style={{ color: "#faad14", marginRight: 4 }} />
                  <strong>{kpis.proyectos_sin_actualizar}</strong> sin actualizar ({">"}7 días)
                </Text>
              )}
              {kpis.cai_pendientes > 0 && (
                <Text>
                  <WarningOutlined style={{ color: "#faad14", marginRight: 4 }} />
                  <strong>{kpis.cai_pendientes}</strong> validaciones CAI pendientes
                </Text>
              )}
            </Space>
          }
        />
      )}

      {/* ── KPIs principales ─────────────────────────────────────────────── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={8} xl={4}>
          <KPICard
            title="Solicitudes activas"
            value={kpis?.total_solicitudes_activas ?? 0}
            icon={<ThunderboltOutlined />}
            color="#1a1f5e"
            onClick={() => navigate("/solicitudes")}
          />
        </Col>
        <Col xs={12} sm={8} xl={4}>
          <KPICard
            title="Proyectos activos"
            value={kpis?.total_proyectos_activos ?? 0}
            icon={<ProjectOutlined />}
            color="#1a1f5e"
            onClick={() => navigate("/proyectos")}
          />
        </Col>
        <Col xs={12} sm={8} xl={4}>
          <KPICard
            title="QW entregados (semana)"
            value={kpis?.quick_wins_semana ?? 0}
            icon={<CheckCircleOutlined />}
            color="#52c41a"
            onClick={() => navigate("/quick-wins")}
          />
        </Col>
        <Col xs={12} sm={8} xl={4}>
          <KPICard
            title="Oportunidades activas"
            value={kpis?.oportunidades_activas ?? 0}
            icon={<TrophyOutlined />}
            color="#1a1f5e"
            onClick={() => navigate("/oportunidades")}
          />
        </Col>
        <Col xs={12} sm={8} xl={4}>
          <KPICard
            title="Sin actualizar (+7d)"
            value={kpis?.proyectos_sin_actualizar ?? 0}
            icon={<ClockCircleOutlined />}
            color="#faad14"
            alert
          />
        </Col>
        <Col xs={12} sm={8} xl={4}>
          <KPICard
            title="CAI pendientes"
            value={kpis?.cai_pendientes ?? 0}
            icon={<WarningOutlined />}
            color="#ff4d4f"
            alert
          />
        </Col>
      </Row>

      {/* ── RAG + Hitos ──────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <RAGGauge
            verde={rag["Verde"]}
            ambar={rag["Ámbar"]}
            rojo={rag["Rojo"]}
          />
        </Col>

        <Col xs={24} md={16}>
          <Card
            size="small"
            title={<Text strong style={{ fontSize: 13 }}>Próximos hitos (14 días)</Text>}
            extra={
              <Button
                type="link" size="small" icon={<ArrowRightOutlined />}
                onClick={() => navigate("/proyectos")}
              >
                Ver proyectos
              </Button>
            }
            style={{ height: "100%" }}
          >
            {!kpis?.proximos_hitos?.length ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#aaa" }}>
                <CheckCircleOutlined style={{ fontSize: 24, marginBottom: 8, display: "block" }} />
                <Text type="secondary">No hay hitos en los próximos 14 días</Text>
              </div>
            ) : (
              <Table
                dataSource={kpis.proximos_hitos}
                rowKey="nombre"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Hito",
                    dataIndex: "nombre",
                    ellipsis: true,
                  },
                  {
                    title: "Fecha",
                    dataIndex: "fecha",
                    width: 110,
                    render: (f: string) => {
                      const dias = dayjs(f).diff(dayjs(), "day");
                      const color = dias <= 3 ? "#ff4d4f" : dias <= 7 ? "#faad14" : "#1a1f5e";
                      return (
                        <Tooltip title={`En ${dias} día${dias !== 1 ? "s" : ""}`}>
                          <Text style={{ color, fontSize: 12 }}>
                            {dayjs(f).format("DD/MM")}
                          </Text>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    title: "",
                    width: 80,
                    render: (_: unknown, record: any) => (
                      <Button
                        type="link" size="small"
                        onClick={() => navigate(`/proyectos/${record.proyecto_id}`)}
                      >
                        Ver
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* ── Acciones rápidas ─────────────────────────────────────────────── */}
      <Card
        size="small"
        title={<Text strong style={{ fontSize: 13 }}>Acciones rápidas</Text>}
        style={{ marginTop: 16 }}
      >
        <Space wrap>
          <Button icon={<ThunderboltOutlined />} onClick={() => navigate("/solicitudes")}>
            Nueva solicitud
          </Button>
          <Button icon={<ProjectOutlined />} onClick={() => navigate("/proyectos")}>
            Nuevo proyecto
          </Button>
          <Button icon={<TrophyOutlined />} onClick={() => navigate("/oportunidades")}>
            Nueva oportunidad
          </Button>
          <Button
            icon={<RiseOutlined />}
            style={{ borderColor: "#1a1f5e", color: "#1a1f5e" }}
            onClick={() => navigate("/reporting")}
          >
            Reporte semanal PDF
          </Button>
        </Space>
      </Card>
    </div>
  );
}
